/**
 * Personas: the service accounts agent playbooks run as. An administrator
 * creates one, gives it a profile (role), and assigns it playbooks — each with
 * its inputs, a run cycle and a credit budget per cycle.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AgentsRuntime, Assignment, Cycle, Persona, PlaybookSummary } from './contract'
import { C, Chip, Empty, cardStyle, formatRelative, formatWhen, inputStyle, labelStyle } from '../jobs/ui'
import { BudgetBar, CYCLE_DAYS, describeCycle, smallBtn } from './bits'

function CycleEditor({ value, onChange }: { value: Cycle; onChange(c: Cycle): void }) {
  const set = (patch: Partial<Cycle>) => onChange({ ...value, ...patch })
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <select value={value.type} onChange={(e) => set({ type: e.target.value as Cycle['type'] })}
        style={{ ...inputStyle, width: 150 }}>
        <option value="manual">Only when started</option>
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
        <option value="monthly">Monthly</option>
        <option value="interval">Every N…</option>
      </select>
      {['daily', 'weekly', 'monthly'].includes(value.type) && (
        <input type="time" value={value.time || '09:00'} onChange={(e) => set({ time: e.target.value })}
          style={{ ...inputStyle, width: 120 }} />
      )}
      {value.type === 'weekly' && CYCLE_DAYS.map((d) => {
        const on = (value.days || []).includes(d)
        return (
          <button key={d} type="button" onClick={() => set({
            days: on ? (value.days || []).filter((x) => x !== d) : [...(value.days || []), d],
          })} style={{ ...smallBtn(on), height: 28, padding: '0 8px' }}>{d}</button>
        )
      })}
      {value.type === 'monthly' && (
        <input type="number" min={1} max={28} value={value.day_of_month || 1}
          onChange={(e) => set({ day_of_month: Number(e.target.value) })}
          style={{ ...inputStyle, width: 80 }} title="Day of month" />
      )}
      {value.type === 'interval' && (
        <>
          <input type="number" min={1} value={value.interval_value || 1}
            onChange={(e) => set({ interval_value: Number(e.target.value) })}
            style={{ ...inputStyle, width: 80 }} />
          <select value={value.interval_unit || 'hours'}
            onChange={(e) => set({ interval_unit: e.target.value as Cycle['interval_unit'] })}
            style={{ ...inputStyle, width: 120 }}>
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
        </>
      )}
      {value.type !== 'manual' && value.type !== 'interval' && (
        <input value={value.timezone || 'UTC'} onChange={(e) => set({ timezone: e.target.value })}
          placeholder="Timezone" style={{ ...inputStyle, width: 150 }} title="IANA timezone, e.g. Asia/Kolkata" />
      )}
    </div>
  )
}

function AssignForm({ runtime, persona, playbooks, onDone }: {
  runtime: AgentsRuntime
  persona: Persona
  playbooks: PlaybookSummary[]
  onDone(): void
}) {
  const [name, setName] = useState(playbooks[0]?.name || '')
  const [budget, setBudget] = useState('50')
  const [cycle, setCycle] = useState<Cycle>({ type: 'manual' })
  const [inputs, setInputs] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const pb = playbooks.find((p) => p.name === name)
  const roleOk = !pb?.required_role || (persona.roles || []).includes(pb.required_role)

  const save = async () => {
    setBusy(true)
    try {
      await runtime.assign(persona.persona_id, {
        playbook: name, cycle, budget_credits: Number(budget),
        inputs: Object.fromEntries(Object.entries(inputs).filter(([, v]) => v !== '')),
      })
      runtime.notify?.('Playbook assigned.', 'success')
      onDone()
    } catch (err) {
      runtime.notify?.(err instanceof Error ? err.message : 'Could not assign it.', 'error')
    } finally {
      setBusy(false)
    }
  }

  if (!playbooks.length) {
    return <div style={{ color: C.ink2, fontSize: '0.9125rem' }}>This application has no playbooks yet — they are designed in the builder.</div>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12,
      background: C.s2, borderRadius: 8 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={labelStyle}>Playbook</span>
        <select value={name} onChange={(e) => setName(e.target.value)} style={inputStyle}>
          {playbooks.map((p) => <option key={p.name} value={p.name}>{p.title}</option>)}
        </select>
      </label>
      {pb && <div style={{ fontSize: '0.8625rem', color: C.ink2 }}>{pb.goal}</div>}
      {!roleOk && (
        <div style={{ fontSize: '0.8625rem', color: C.warn }}>
          This playbook needs the <code>{pb?.required_role}</code> profile. Add it to {persona.name} first.
        </div>
      )}
      {(pb?.inputs || []).map((i) => (
        <label key={i.name} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>{i.label || i.name}{i.required && i.default === undefined ? ' *' : ''}</span>
          <input value={inputs[i.name] ?? (i.default != null ? String(i.default) : '')}
            onChange={(e) => setInputs((cur) => ({ ...cur, [i.name]: e.target.value }))} style={inputStyle} />
        </label>
      ))}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={labelStyle}>Run cycle</span>
        <CycleEditor value={cycle} onChange={setCycle} />
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 220 }}>
        <span style={labelStyle}>AI credit budget per cycle</span>
        <input type="number" min={1} value={budget} onChange={(e) => setBudget(e.target.value)} style={inputStyle} />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" disabled={busy || !roleOk || !name} onClick={() => void save()}
          style={{ ...smallBtn(true), opacity: busy || !roleOk ? 0.6 : 1 }}>Assign</button>
        <button type="button" onClick={onDone} style={smallBtn()}>Cancel</button>
      </div>
    </div>
  )
}

function AssignmentRow({ runtime, a, title, onChange }: {
  runtime: AgentsRuntime; a: Assignment; title: string; onChange(): void
}) {
  const [busy, setBusy] = useState(false)
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true)
    try { await fn(); runtime.notify?.(ok, 'success'); onChange() } catch (err) {
      runtime.notify?.(err instanceof Error ? err.message : 'That did not work.', 'error')
    } finally { setBusy(false) }
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 0',
      borderTop: `1px solid ${C.line}` }}>
      <div style={{ flex: '1 1 180px', minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.9125rem' }}>{title}</div>
        <div style={{ fontSize: '0.8125rem', color: C.ink2 }}>
          {describeCycle(a.cycle)}
          {a.next_run_at && a.status === 'active' ? ` · next ${formatRelative(a.next_run_at)}` : ''}
          {a.last_run_at ? ` · last ${formatWhen(a.last_run_at)}` : ''}
        </div>
      </div>
      <BudgetBar spent={a.cycle_spent} budget={a.budget_credits} />
      {a.status !== 'active' && <Chip tone="muted">Paused</Chip>}
      <button type="button" disabled={busy} style={smallBtn(true)}
        onClick={() => void run(() => runtime.runNow(a.assign_id), 'Run started.')}>Run now</button>
      <button type="button" disabled={busy} style={smallBtn()}
        onClick={() => void run(() => runtime.updateAssignment(a.assign_id, { active: a.status !== 'active' }),
          a.status === 'active' ? 'Cycle paused.' : 'Cycle resumed.')}>
        {a.status === 'active' ? 'Pause cycle' : 'Resume cycle'}
      </button>
      <button type="button" disabled={busy} style={smallBtn()}
        onClick={() => {
          const v = window.prompt('Credit budget per cycle', String(a.budget_credits))
          if (v != null && Number(v) > 0) {
            void run(() => runtime.updateAssignment(a.assign_id, { budget_credits: Number(v) }), 'Budget updated.')
          }
        }}>Budget</button>
      <button type="button" disabled={busy} style={{ ...smallBtn(), color: C.err }}
        onClick={() => void run(() => runtime.unassign(a.assign_id), 'Assignment removed.')}>Remove</button>
    </div>
  )
}

export default function PersonasView({ runtime }: { runtime: AgentsRuntime }) {
  const [personas, setPersonas] = useState<Persona[] | null>(null)
  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([])
  const [roles, setRoles] = useState<string[]>([])
  const [error, setError] = useState('')
  const [assigning, setAssigning] = useState('')
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ name: '', description: '', roles: [] as string[] })

  const load = useCallback(async () => {
    try {
      const [p, pb, r] = await Promise.all([runtime.listPersonas(), runtime.listPlaybooks(), runtime.listRoles()])
      setPersonas(p)
      setPlaybooks(pb)
      setRoles(r.filter((x) => !['admin', 'func-admin'].includes(x.toLowerCase())))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the personas.')
    }
  }, [runtime])

  useEffect(() => { void load() }, [load])

  const titles = useMemo(() => Object.fromEntries(playbooks.map((p) => [p.name, p.title])), [playbooks])
  const suggested = useMemo(() => Array.from(new Set(playbooks.map((p) => p.required_role).filter(Boolean))) as string[], [playbooks])

  const create = async () => {
    try {
      await runtime.createPersona(draft)
      runtime.notify?.('Persona created.', 'success')
      setCreating(false)
      setDraft({ name: '', description: '', roles: [] })
      await load()
    } catch (err) {
      runtime.notify?.(err instanceof Error ? err.message : 'Could not create it.', 'error')
    }
  }

  const toggle = async (p: Persona) => {
    try {
      await runtime.updatePersona(p.persona_id, { active: p.status !== 'active' })
      await load()
    } catch (err) {
      runtime.notify?.(err instanceof Error ? err.message : 'Could not change it.', 'error')
    }
  }

  const remove = async (p: Persona) => {
    if (!window.confirm(`Remove ${p.name}? Its assignments go with it; its past runs stay in the history.`)) return
    try {
      await runtime.deletePersona(p.persona_id)
      await load()
    } catch (err) {
      runtime.notify?.(err instanceof Error ? err.message : 'Could not remove it.', 'error')
    }
  }

  const roleOptions = Array.from(new Set([...suggested, ...roles]))

  if (error) return <div style={{ color: C.err }}>{error}</div>
  if (!personas) return <div style={{ color: C.ink2 }}>Loading…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, color: C.ink }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <p style={{ margin: 0, flex: 1, fontSize: '0.875rem', color: C.ink2, lineHeight: 1.5 }}>
          A persona is a service account an agent playbook runs as. It never signs in; it holds a profile,
          and everything it does is checked against that profile and recorded under its name.
        </p>
        {!creating && <button type="button" onClick={() => setCreating(true)} style={smallBtn(true)}>New persona</button>}
      </div>

      {creating && (
        <div style={{ ...cardStyle, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input placeholder="Name, e.g. Collections Agent" value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })} style={inputStyle} />
          <input placeholder="What it is for (optional)" value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })} style={inputStyle} />
          <div>
            <div style={{ ...labelStyle, marginBottom: 6 }}>Profiles</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {roleOptions.map((r) => {
                const on = draft.roles.includes(r)
                return (
                  <button key={r} type="button" style={{ ...smallBtn(on), height: 28 }}
                    onClick={() => setDraft({ ...draft, roles: on ? draft.roles.filter((x) => x !== r) : [...draft.roles, r] })}>
                    {r}{suggested.includes(r) ? ' ★' : ''}
                  </button>
                )
              })}
              {!roleOptions.length && <span style={{ color: C.ink2, fontSize: '0.8625rem' }}>No profiles yet.</span>}
            </div>
            {suggested.length > 0 && (
              <div style={{ fontSize: '0.8125rem', color: C.faint, marginTop: 4 }}>★ proposed by a playbook</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" disabled={!draft.name || !draft.roles.length}
              onClick={() => void create()} style={{ ...smallBtn(true), opacity: !draft.name || !draft.roles.length ? 0.6 : 1 }}>Create</button>
            <button type="button" onClick={() => setCreating(false)} style={smallBtn()}>Cancel</button>
          </div>
        </div>
      )}

      {personas.length === 0 && !creating && (
        <Empty title="No personas yet" hint="Create one, give it the profile a playbook proposes, and assign the playbook with a run cycle and a credit budget." />
      )}

      {personas.map((p) => (
        <div key={p.persona_id} style={{ ...cardStyle, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="material-icons" aria-hidden style={{ fontSize: 21, color: C.accent }}>smart_toy</span>
            <span style={{ fontWeight: 700 }}>{p.name}</span>
            {(p.roles || []).map((r) => <Chip key={r} tone="accent">{r}</Chip>)}
            {p.status !== 'active' && <Chip tone="muted">Switched off</Chip>}
            <span style={{ flex: 1 }} />
            <button type="button" onClick={() => setAssigning(assigning === p.persona_id ? '' : p.persona_id)}
              style={smallBtn()}>Assign playbook</button>
            <button type="button" onClick={() => void toggle(p)} style={smallBtn()}>
              {p.status === 'active' ? 'Switch off' : 'Switch on'}
            </button>
            <button type="button" onClick={() => void remove(p)} style={{ ...smallBtn(), color: C.err }}>Remove</button>
          </div>
          {p.description && <div style={{ fontSize: '0.8625rem', color: C.ink2, marginTop: 4 }}>{p.description}</div>}
          {assigning === p.persona_id && (
            <div style={{ marginTop: 10 }}>
              <AssignForm runtime={runtime} persona={p} playbooks={playbooks}
                onDone={() => { setAssigning(''); void load() }} />
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            {(p.assignments || []).map((a) => (
              <AssignmentRow key={a.assign_id} runtime={runtime} a={a}
                title={titles[a.playbook] || a.playbook} onChange={() => void load()} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
